import {
  DeleteOutlined,
  EditOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Flex, Space, Tooltip, Typography } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import { type Dispatch, type SetStateAction } from 'react';

import type { ModelSocialIntegration } from '@app/api/SocialIntegrations';
import { Avatar } from '@app/components/Avatar';
import {
  YOOKASSA,
  ACTIVE,
  ATC_BEELINE,
  ATC_MANGO,
  AUTHORIZING,
  BANNED,
  EMAIL_HANDLER,
  ERROR,
  FRONT_WARNING,
  INSTAGRAM,
  TELEGRAM,
  TELEGRAM_PERSONAL,
  U_ACTION,
  U_INTEGRATIONS,
  U_KEY,
  U_LOGIN,
  U_SALES_FUNNELS,
  U_STATUS,
  VK,
  WAREHOUSE_1C,
  WHATSAPP,
  INITIALIZED,
  MAX_MESSENGER,
  LOYALTY_PROGRAM_SENDOUTS,
  SMS_SERVICE,
  TECH_SUPPORT,
} from '@app/constants';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { IntegrationStatus } from '@app/shared/ui/IntegrationStatus';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';

import type { EditMode } from '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal/types';

import '@pages/Integrations/IntegrationsList/styles/Integrations.desktop.scss';

const formatDate = (inputDate: string | null) => {
  if (!inputDate) return '';
  const date = new Date(inputDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const renderIntegrationDetails = (
  socialType: string | null,
  accountLogin: string | null,
  accountName: string | null
) => {
  if (!socialType) return null;

  let value: string | null = null;

  switch (socialType) {
    case INSTAGRAM:
      value = accountName;
      break;

    case ATC_BEELINE:
      value = null;
      break;

    case EMAIL_HANDLER:
    case VK:
    case TELEGRAM:
    case ATC_MANGO:
    case WAREHOUSE_1C:
    case TELEGRAM_PERSONAL:
    case MAX_MESSENGER:
    case LOYALTY_PROGRAM_SENDOUTS:
    case SMS_SERVICE:
      value = accountLogin;
      break;

    default:
      value = accountLogin ? `+${accountLogin}` : null;
  }

  return value ? (
    <>
      <br />
      {value}
    </>
  ) : null;
};

export const adaptDataToTable = (
  integrations: ModelSocialIntegration[],
  setDeletingModalOpen: (status: boolean) => void,
  setCurrentDeletingId: (id: number) => void,
  setUpdatingIntegrationId: (id: number) => void,
  setIsReloadModalOpen: (status: boolean) => void,
  handleEdit: (id: number) => void,
  hasUserRightToEditIntegration: boolean,
  hasUserRightToDeleteIntegration: boolean,
  setIsOAuthFrameOpen: (value: boolean) => void,
  setCurrentAuthLink: (value: string) => void,
  showNotification: NNotificationProvider.ShowNotificationType,
  openEmailHandlerParsingSettingsPage: (id: number) => void,
  openEditYooKassaModal: (id: number) => void,
  setEdit1CIntegrationId: Dispatch<SetStateAction<number | null>>,
  setIsEdit1CIntegrationModalOpen: Dispatch<SetStateAction<boolean>>,
  setEdit1CIntegrationMode: Dispatch<SetStateAction<EditMode>>,
  openAuthWhatsAppModal: (id: number) => Promise<void>,
  openConnectAuth: (
    channelType: string,
    integrationId: number,
    authUrl?: string | null
  ) => void,
  openLoyaltyChatSettings?: (integrationId: number) => void
) => {
  return integrations.map((integration) => {
    const {
      Id,
      SocialType,
      CreateUser,
      AccountLogin,
      AccountPhoto,
      Status,
      TimeCreated,
      ErrorMessage,
      SubStatus,
      SocialIntegrationName,
      SalesFunnel,
      SalesFunnelStage,
      AccountName,
      SocialTypeUserFriendly,
      OAuthUrl,
      ParseUsingNeural = false,
    } = integration || {};

    const openEditIntegrationModal = () => {
      if (!OAuthUrl) {
        return showNotification('Ссылка не найдена', FRONT_WARNING);
      }

      setIsOAuthFrameOpen(true);
      setCurrentAuthLink(OAuthUrl);
    };

    const handleSettingsButtonClick = () => {
      switch (SocialType) {
        case EMAIL_HANDLER:
          openEmailHandlerParsingSettingsPage(Id);
          break;
        case 'warehouse-1c':
          setEdit1CIntegrationId(Id);
          setEdit1CIntegrationMode('options');
          setIsEdit1CIntegrationModalOpen(true);
          break;
        // Вход в этих каналах идёт в окне CRM (us/10721), OAuth-ссылка им
        // не нужна — открываем единое окно подключения.
        case TELEGRAM:
        case TELEGRAM_PERSONAL:
        case MAX_MESSENGER:
          openConnectAuth(SocialType, Id);
          break;
        // ВКонтакте и Instagram: единое окно + вход у провайдера в отдельном
        // окне браузера по OAuthUrl (task/10731).
        case VK:
        case INSTAGRAM:
          if (!OAuthUrl) {
            showNotification('Ссылка не найдена', FRONT_WARNING);
            break;
          }
          openConnectAuth(SocialType, Id, OAuthUrl);
          break;
        default:
          openEditIntegrationModal();
          break;
      }
    };

    const handleEditIntegration = (id: number) => {
      switch (SocialType) {
        case 'warehouse-1c':
          setEdit1CIntegrationId(id);
          setEdit1CIntegrationMode('edit');
          setIsEdit1CIntegrationModalOpen(true);
          break;
        case YOOKASSA:
          openEditYooKassaModal(id);
          break;
        default:
          handleEdit(id);
          break;
      }
    };

    const getShowEditIntegration = () => {
      switch (SocialType) {
        case INSTAGRAM:
        case VK:
        case TELEGRAM:
        case TELEGRAM_PERSONAL:
        case MAX_MESSENGER:
          return true;
        case EMAIL_HANDLER:
          return ParseUsingNeural;
        case LOYALTY_PROGRAM_SENDOUTS:
        case 'warehouse-1c':
          return false;
        default:
          return false;
      }
    };

    const getRestartIntegrationButton = (
      SocialType: ModelSocialIntegration['SocialType'],
      Status: ModelSocialIntegration['Status']
    ) => {
      // У ЮKassa перезагрузки не существует: общий RestartIntegration ищет
      // по id в SocialIntegrations, а ЮKassa живёт в PaymentSystemIntegration —
      // id пересекаются, и запрос попадал в чужую интеграцию (issue/10609).
      if (
        SocialType !== INSTAGRAM &&
        SocialType !== 'warehouse-1c' &&
        SocialType !== YOOKASSA
      )
        if (Status === ACTIVE || Status === ERROR) return true;

      if (SocialType === TELEGRAM_PERSONAL || SocialType === MAX_MESSENGER) {
        if (Status === ACTIVE || Status === ERROR || Status === INITIALIZED)
          return true;
      }
    };

    return {
      // Список склеен из четырёх источников с НЕЗАВИСИМЫМИ последовательностями
      // Id: соцсети, платёжные системы (ЮKassa), 1С-Склад и лояльность. Один
      // только Id ключом не годится — совпадение id между источниками даёт
      // React две строки с одинаковым ключом (issue/10852).
      [U_KEY]: `${SocialType}-${Id}`,
      [U_INTEGRATIONS]: (
        <Flex gap={12} align="center" style={{ minWidth: '180px' }}>
          <SocialIntegrationIcon type={SocialType} size="lg" />
          <div>
            {SocialTypeUserFriendly ? (
              <>
                <span data-testid="integration-type">
                  {SocialTypeUserFriendly}
                </span>
                <br />
              </>
            ) : null}
            <Typography.Text
              type="secondary"
              data-testid="fullname-who-created-integration"
            >
              {CreateUser}
            </Typography.Text>
            <br />
            <Typography.Text
              type="secondary"
              data-testid="date-time-integration-created"
            >
              {formatDate(TimeCreated)}
            </Typography.Text>
          </div>
        </Flex>
      ),
      [U_LOGIN]: (
        <div
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={AccountPhoto}
            nameFirst={SocialIntegrationName}
            className="image"
            size={40}
            round
          />
          <div>
            <Tooltip title={SocialIntegrationName}>
              <Paragraph
                ellipsis={{ rows: 1 }}
                className="integration-login"
                strong
              >
                {SocialIntegrationName}
              </Paragraph>
            </Tooltip>
            <span data-testid="account-integration-name">
              {renderIntegrationDetails(SocialType, AccountLogin, AccountName)}
            </span>
          </div>
        </div>
      ),
      [U_SALES_FUNNELS]: (
        <div
          style={{
            minWidth: '130px',
          }}
        >
          {SalesFunnel && SalesFunnelStage ? (
            <>
              <span data-testid="funnel-name">{SalesFunnel}</span>{' '}
              <span data-testid="funnel-stage-name">{SalesFunnelStage}</span>
            </>
          ) : (
            ''
          )}
        </div>
      ),
      [U_STATUS]: (
        <div
          style={{
            minWidth: '130px',
          }}
          data-testid="integration-status"
        >
          <IntegrationStatus
            status={Status}
            errorMessage={ErrorMessage}
            subStatus={SubStatus}
          />
        </div>
      ),
      [U_ACTION]:
        SocialType === LOYALTY_PROGRAM_SENDOUTS ? (
          openLoyaltyChatSettings ? (
            <Tooltip title="Настроить Рекомендуй">
              <Button
                icon={<SettingOutlined />}
                onClick={() => openLoyaltyChatSettings(Id)}
                data-testid="recommend-chat-settings"
              />
            </Tooltip>
          ) : (
            <></>
          )
        ) : (
          <Space>
            {hasUserRightToEditIntegration && Status !== BANNED && (
              <Tooltip title="Редактировать интеграцию">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    handleEditIntegration(Id);
                  }}
                  data-testid="edit-integration"
                />
              </Tooltip>
            )}
            {/* Вход по QR доступен и из «Ошибки»: у WhatsApp нет кнопки
                настройки, поэтому интеграция с мёртвой сессией оставалась без
                выхода — только удалить (issue/10916). Основное лечение на
                стороне шлюза (он переводит такой аккаунт в «Подключается»),
                эта ветка — страховка, когда до «Подключается» дело не дошло. */}
            {(Status === AUTHORIZING || Status === ERROR) &&
              SocialType === WHATSAPP && (
                <Tooltip title="Авторизовать устройство">
                  <Button
                    icon={<QrcodeOutlined />}
                    onClick={() => {
                      openAuthWhatsAppModal(Id);
                    }}
                  />
                </Tooltip>
              )}
            {getRestartIntegrationButton(SocialType, Status) && (
              <Tooltip title="Перезагрузить интеграцию">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setIsReloadModalOpen(true);
                    setUpdatingIntegrationId(Id);
                  }}
                  data-testid="restart-integration"
                />
              </Tooltip>
            )}
            {getShowEditIntegration() && (
              <Tooltip title="Настроить интеграцию">
                <Button
                  icon={<SettingOutlined />}
                  onClick={handleSettingsButtonClick}
                  data-testid="settings-integration"
                />
              </Tooltip>
            )}
            {/* «Техническая поддержка» — системная интеграция (US 11021):
                удаление запрещено, опцию скрываем. */}
            {hasUserRightToDeleteIntegration && SocialType !== TECH_SUPPORT && (
              <Tooltip title="Удалить интеграцию">
                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setCurrentDeletingId(Id);
                    setDeletingModalOpen(true);
                  }}
                  data-testid="delete-integration"
                />
              </Tooltip>
            )}
          </Space>
        ),
    };
  });
};
